import React, { useState, useRef } from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import { media } from "src/styles/breakpoints"
import { GatsbyImage } from "gatsby-plugin-image"
import BaseIcon from "src/components/Base/BaseIcon"

const ResourceTile = ({
  id,
  path,
  title,
  type,
  numberOfFiles,
  country,
  year,
  showDateAdded,
  dateAdded,
  imageSharp,
  imageSharpSequence,
  className,
}) => {
  const [isHover, setIsHover] = useState(false)
  const wrapper = useRef()

  function handleMouseEnter() {
    setIsHover(true)
    let i = 2
    wrapper.current.setAttribute("data-preview-current", 1)
    window.sequenceInterval = setInterval(() => {
      wrapper.current && wrapper.current.setAttribute("data-preview-current", i)
      i = i >= imageSharpSequence.length - 1 ? 0 : i + 1
    }, 200)
  }

  function handleMouseLeave() {
    setIsHover(false)
    clearInterval(window.sequenceInterval)
    window.sequenceInterval = undefined
    wrapper.current.removeAttribute("data-preview-current")
  }

  return (
    <Wrapper
      ref={wrapper}
      className={className}
      to={path}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageWrapper>
        {imageSharpSequence ? (
          isHover ? (
            <ImageSequence>
              {imageSharpSequence.map(
                (img, index) =>
                  img && (
                    <Image
                      image={img.gatsbyImageData}
                      alt={title}
                      style={{
                        width: "100%",
                        position: "absolute",
                      }}
                      key={index}
                    />
                  )
              )}
            </ImageSequence>
          ) : (
            <Image
              image={imageSharpSequence[0].gatsbyImageData}
              alt={title}
              style={{ width: "100%", position: "absolute" }}
            />
          )
        ) : (
          imageSharp && (
            <Image
              image={imageSharp.gatsbyImageData}
              alt={title}
              style={{ width: "100%", position: "absolute" }}
            />
          )
        )}
        <Type>
          {type === "Video" && (
            <>
              <TypeIcon name="video" />{" "}
              {numberOfFiles > 1 ? `${numberOfFiles} Videos` : `Video`}
            </>
          )}
          {type === "Picture" && (
            <>
              <TypeIcon name="picture" /> {numberOfFiles} Photos
            </>
          )}
          {type === "Document" && (
            <>
              <TypeIcon name="document" /> Document
            </>
          )}
        </Type>
      </ImageWrapper>
      <Content>
        <Title>{title}</Title>
        {((showDateAdded && dateAdded) || country || year) && (
          <Details>
            {showDateAdded && dateAdded ? (
              <span>Added on {dateAdded}</span>
            ) : (
              <>
                {country}
                {country && year && ", "}
                {year}
              </>
            )}
          </Details>
        )}
        <DateOnHover>Added on {dateAdded}</DateOnHover>
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled(Link)`
  display: block;
  position: relative;
  border: 1px solid #cfdbdb;
  border-radius: 3px;
  transition: 0.15s;

  &:hover {
    box-shadow: 0 40px 60px rgba(0, 11, 33, 0.06);
    border-color: #3ab6da;
  }
`

const ImageWrapper = styled.div`
  padding-top: 56.25%;
  margin: -1px -1px 0;
  position: relative;
  z-index: 1;

  &::after {
    content: "";
    width: 100%;
    height: 75px;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 2;
    background: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.6));
  }

  .gatsby-image-wrapper {
    top: 0;
    left: 0;
  }
`

const Image = styled(GatsbyImage)`
  max-height: 100%;

  &,
  img {
    border-radius: 3px 3px 0 0;
  }
`

const ImageSequence = styled.div`
  ${Image}:first-child {
    z-index: 1;
  }

  ${Image}:nth-child(2) {
    [data-preview-current="1"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(3) {
    [data-preview-current="2"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(4) {
    [data-preview-current="3"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(5) {
    [data-preview-current="4"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(6) {
    [data-preview-current="5"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(7) {
    [data-preview-current="6"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(8) {
    [data-preview-current="7"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(9) {
    [data-preview-current="8"]${Wrapper} & {
      z-index: 1;
    }
  }
  ${Image}:nth-child(10) {
    [data-preview-current="9"]${Wrapper} & {
      z-index: 1;
    }
  }
`

const Type = styled.h4`
  position: absolute;
  bottom: 15px;
  left: 25px;
  z-index: 3;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 11px;
  color: #fff;
`

const TypeIcon = styled(BaseIcon)`
  width: 12px;
  height: 12px;
  margin-right: 5px;
`

const Title = styled.h3`
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: #000;

  ${media.tablet} {
    font-size: 15px;
  }
`

const Content = styled.div`
  padding: 15px;

  ${media.tablet} {
    padding: 25px;
  }
`

const Details = styled.p`
  margin-top: 7px;
  font-size: 13px;
  color: #b4b3b8;
  /* transition: opacity 0.15s; */

  ${Wrapper}:hover & {
    /* opacity: 0; */
  }
`

const DateOnHover = styled.p`
  opacity: 0;
  width: 100%;
  padding: 3px 15px;
  position: absolute;
  bottom: 22px;
  left: 0;
  background: #fff;
  font-size: 13px;
  color: #b4b3b8;
  transition: opacity 0.15s;

  ${Wrapper}:hover & {
    opacity: 1;
  }

  ${media.tablet} {
    padding-left: 25px;
    padding-right: 25px;
  }
`

export default ResourceTile
